import { observer } from 'mobx-react-lite';
import * as React from 'react';
import MongoConsoleStore from '../stores/MongoConsoleStore';
import { Button, Input, InputNumber, Layout, Select, Space, Table, Alert, Spin, Typography } from 'antd';
import LayoutHeader from '../../../components/LayoutHeader';
import { Content } from 'antd/lib/layout/layout';
import { PlayCircleOutlined, ClearOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

type Props = {
    store: MongoConsoleStore;
};

const MongoConsolePanel: React.FC<Props> = ({ store }) => {
    React.useEffect(() => {
        if (store.databases.length === 0) {
            store.loadDatabases();
        }
    }, [store]);

    const renderResultsTable = () => {
        if (store.executionError) {
            return (
                <Alert
                    message="Query Error"
                    description={store.executionError}
                    type="error"
                    showIcon
                    style={{ marginTop: 16 }}
                />
            );
        }

        if (!store.hasResults) {
            return null;
        }

        const columns = store.columns.map(col => ({
            title: col,
            dataIndex: col,
            key: col,
            ellipsis: true,
            render: (value: unknown) => {
                if (value === null || value === undefined) {
                    return <Text type="secondary">null</Text>;
                }
                if (typeof value === 'object') {
                    return <Text code>{JSON.stringify(value)}</Text>;
                }
                return String(value);
            }
        }));

        return (
            <div style={{ marginTop: 16 }}>
                <Space style={{ marginBottom: 8 }}>
                    <Text strong>
                        {store.documentCount} document{store.documentCount !== 1 ? 's' : ''} returned
                    </Text>
                    <Text type="secondary">({store.executionTimeMs}ms)</Text>
                </Space>
                <Table
                    data-id="mongo-console-results-table"
                    className="alpha-portal-table"
                    dataSource={store.results.slice()}
                    columns={columns}
                    rowKey={(_, index) => String(index)}
                    pagination={{
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} documents`
                    }}
                    scroll={{ x: 'max-content' }}
                    size="small"
                    sticky
                />
            </div>
        );
    };

    return (
        <Layout style={{ height: '100%', background: 'white' }}>
            <LayoutHeader
                title="Mongo Console"
                helpMessage="Execute read-only MongoDB queries against alpha-intelligence and alpha-iota databases."
            />
            <Layout>
                <Content
                    style={{
                        maxHeight: 'calc(100vh - 70px)',
                        overflowY: 'auto',
                        padding: '16px 24px'
                    }}
                >
                    <Spin spinning={store.isExecuting} tip="Executing query...">
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <Space size="middle" wrap>
                                <div>
                                    <div style={{ marginBottom: 4 }}>
                                        <Text strong>Database</Text>
                                    </div>
                                    <Select
                                        data-id="mongo-console-database-select"
                                        placeholder="Select database"
                                        value={store.selectedDatabase}
                                        onChange={(value: string) => store.setSelectedDatabase(value)}
                                        loading={store.isLoadingDatabases}
                                        style={{ width: 250 }}
                                    >
                                        {store.databases.map(db => (
                                            <Option key={db} value={db}>
                                                {db}
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                                <div>
                                    <div style={{ marginBottom: 4 }}>
                                        <Text strong>Collection</Text>
                                    </div>
                                    <Select
                                        data-id="mongo-console-collection-select"
                                        placeholder="Select collection"
                                        value={store.selectedCollection}
                                        onChange={(value: string) => store.setSelectedCollection(value)}
                                        loading={store.isLoadingCollections}
                                        disabled={!store.selectedDatabase}
                                        style={{ width: 250 }}
                                        showSearch
                                        filterOption={(input, option) =>
                                            (option?.children as unknown as string)
                                                ?.toLowerCase()
                                                .includes(input.toLowerCase())
                                        }
                                    >
                                        {store.collections.map(col => (
                                            <Option key={col} value={col}>
                                                {col}
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                                <div>
                                    <div style={{ marginBottom: 4 }}>
                                        <Text strong>Limit</Text>
                                    </div>
                                    <InputNumber
                                        data-id="mongo-console-limit-input"
                                        min={1}
                                        max={100}
                                        value={store.limit}
                                        onChange={value => store.setLimit(value || 100)}
                                        style={{ width: 120 }}
                                    />
                                </div>
                            </Space>

                            <div style={{ marginTop: 10 }}>
                                <div style={{ marginBottom: 4 }}>
                                    <Text strong>Filter (JSON)</Text>
                                </div>
                                <TextArea
                                    data-id="mongo-console-filter-input"
                                    placeholder='e.g. { "name": "example" }'
                                    value={store.filter}
                                    onChange={e => store.setFilter(e.target.value)}
                                    rows={3}
                                    style={{ fontFamily: 'monospace' }}
                                />
                            </div>

                            <div>
                                <div style={{ marginBottom: 4 }}>
                                    <Text strong>Projection (JSON, optional)</Text>
                                </div>
                                <TextArea
                                    data-id="mongo-console-projection-input"
                                    placeholder='e.g. { "name": 1, "createdDate": 1 }'
                                    value={store.projection}
                                    onChange={e => store.setProjection(e.target.value)}
                                    rows={2}
                                    style={{ fontFamily: 'monospace' }}
                                />
                            </div>

                            <Space>
                                <Button
                                    data-id="mongo-console-execute-button"
                                    type="primary"
                                    icon={<PlayCircleOutlined />}
                                    onClick={() => store.executeQuery()}
                                    disabled={!store.canExecute}
                                    loading={store.isExecuting}
                                >
                                    Execute Query
                                </Button>
                                <Button
                                    data-id="mongo-console-clear-button"
                                    icon={<ClearOutlined />}
                                    onClick={() => store.clearResults()}
                                    disabled={!store.hasResults && !store.executionError}
                                >
                                    Clear Results
                                </Button>
                            </Space>
                        </Space>

                        {renderResultsTable()}
                    </Spin>
                </Content>
            </Layout>
        </Layout>
    );
};

export default observer(MongoConsolePanel);
